// Constants
import { getInitials } from '../../utils/helperMethods';
import downArrowImg from '../../static/images/down-arrow.png';
import { MENU_LIST } from '../../utils/constants/menuConstant';

// Libraries
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Styles
import './sidebar.scss';

// Images
import { Link, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';

const Sidebar = ({ auth }) => {
    const { userDetails, resourceDetails } = auth;
    const currentMatch = useLocation();

    const [activeSubMenu, setActiveSubMenu] = useState(null);
    const [isActiveSubMenuOpen, setIsActiveSubMenuOpen] = useState(true);

    // Helpers for permissions
    const hasAnyPermission = (serverKeys = '', availableResources = []) => {
        return serverKeys
            .split(',')
            .map(key => key.trim())
            .some(key => availableResources.includes(key));
    };

    const hasSubMenuPermission = (serverKeys = '', permissions = []) => {
        return serverKeys
            .split(',')
            .map(key => key.trim())
            .some(key => permissions.includes(key));
    };

    const getSelectedParentIndex = (currentMatch) => {
        let selectedParentIndex = MENU_LIST.findIndex(
            (element) => element.link === currentMatch.pathname
        );

        if (selectedParentIndex === -1) {
            for (let index = 0; index < MENU_LIST.length; index++) {
                const subMenu = MENU_LIST[index]?.subMenu || [];
                const selectedSubMenu = subMenu.findIndex(
                    (element) => element.link === currentMatch.pathname
                );
                if (selectedSubMenu !== -1) {
                    return index;
                }
            }
        }

        return selectedParentIndex;
    };

    const handleSubMenuToggle = (selectedMenuItem) => {
        if (
            activeSubMenu &&
            activeSubMenu?.serverKey === selectedMenuItem?.serverKey
        ) {
            setIsActiveSubMenuOpen(!isActiveSubMenuOpen);
        } else {
            setActiveSubMenu(selectedMenuItem);
            setIsActiveSubMenuOpen(true);
        }
    };

    useEffect(() => {
        const selectedParentIndex = getSelectedParentIndex(currentMatch);
        if (selectedParentIndex !== -1) {
            setActiveSubMenu(MENU_LIST[selectedParentIndex]);
            setIsActiveSubMenuOpen(true);
        }
    }, [currentMatch, currentMatch.pathname]);

    const avatar = getInitials(userDetails.name);

    return (
        <div className="sidebar">
            <div className="user-profile-section">
                <div>
                    <div
                        className="profile-avatar"
                        style={{ background: avatar.colour }}
                    >
                        {avatar.name}
                    </div>
                </div>
                <div className="profile-details">
                    <p className="details-name">{userDetails.name}</p>
                    <p className="details-email">{userDetails.email}</p>
                </div>
            </div>

            <div className="navigation-wrapper">
                {MENU_LIST.map((menuItem, index) => {
                    const parentHasAccess = hasAnyPermission(
                        menuItem.serverKey,
                        resourceDetails.resources || []
                    );

                    if (parentHasAccess && menuItem.subMenu?.length > 0) {
                        // Gather all possible permission keys from the parent
                        const parentKeys = menuItem.serverKey
                            .split(',')
                            .map(k => k.trim());
                        const subModulePermissions = parentKeys.flatMap(
                            key => resourceDetails.permissions?.[key] || []
                        );

                        return (
                            <div className="item-wrapper" key={`sidebar-${index}`}>
                                <div
                                    className={`item ${
                                        menuItem.subMenu.some(sub =>
                                            sub.link === currentMatch.pathname
                                        )
                                            ? 'active'
                                            : ''
                                    }`}
                                    onClick={() => handleSubMenuToggle(menuItem)}
                                >
                                    <div className="item-title">
                                        <div className="icon-wrap">
                                            <img
                                                src={menuItem.imgSrc}
                                                alt=""
                                                className="item-icon"
                                            />
                                        </div>
                                        {menuItem.menuName}
                                    </div>
                                    <img
                                        src={downArrowImg}
                                        alt=""
                                        className={`item-arrow ${
                                            isActiveSubMenuOpen &&
                                            activeSubMenu?.serverKey === menuItem?.serverKey
                                                ? 'rotate'
                                                : ''
                                        }`}
                                    />
                                </div>

                                <div
                                    className={`subitem-wrapper ${
                                        isActiveSubMenuOpen &&
                                        activeSubMenu?.serverKey === menuItem?.serverKey
                                            ? ''
                                            : 'hide'
                                    }`}
                                >
                                    {menuItem.subMenu.map((subMenuItem, subIndex) => {
                                        const hasPermission =
                                            menuItem.isParentLevelPermission ||
                                            hasSubMenuPermission(
                                                subMenuItem.serverKey,
                                                subModulePermissions
                                            );

                                        if (hasPermission) {
                                            return (
                                                <div className="subitem" key={`subitem-${subIndex}`}>
                                                    <span
                                                        className={`${
                                                            currentMatch.pathname === subMenuItem.link
                                                                ? 'active'
                                                                : ''
                                                        }`}
                                                    >
                                                        <Link to={subMenuItem.link}>
                                                            {subMenuItem.menuName}
                                                        </Link>
                                                    </span>
                                                </div>
                                            );
                                        }

                                        return null;
                                    })}
                                </div>
                            </div>
                        );
                    }

                    // Handle top-level menu item (no sub-menu)
                    if (parentHasAccess && !menuItem.subMenu?.length) {
                        return (
                            <div className="item-wrapper" key={`sidebar-${index}`}>
                                <div
                                    className={`item ${
                                        currentMatch.pathname.includes(menuItem.link)
                                            ? 'active'
                                            : ''
                                    }`}
                                >
                                    <div className="item-title">
                                        <div className="icon-wrap">
                                            <img
                                                src={menuItem.imgSrc}
                                                alt=""
                                                className="item-icon"
                                            />
                                        </div>
                                        <Link to={menuItem.link}>
                                            {menuItem.menuName}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    return null;
                })}
            </div>
        </div>
    );
};

const mapStateToProps = ({ auth }) => ({
    auth,
});

Sidebar.propTypes = {
    auth: PropTypes.object,
};

export default connect(mapStateToProps)(Sidebar);
